import {
  ActivityStatusMiddleware,
  BFFWebChat,
  LoadingState,
  adaptiveCardARIARoleMessageMiddleware,
  androidAutoScrollBugMiddleware,
  bffAnswerAttachmentMiddleware,
  bffNubInjectorActivityMiddleware,
  buildMarkdownRenderer,
  getQueryParam,
  mapConversationContext,
  markdownLinkTargetMiddleware,
  openLinkedIntentUrlMiddleware,
  openUrlEventHandlerMiddleware,
  removeKeyboardInstructionStrings,
  skipReactionActivitiesMiddleware,
  useCustomSendbox,
  useGetWebChatConfig,
  usePostConversationStarted,
  useWebChatBootstrap
} from '@sa/bff-webchat-ui';
import type { OliviaDirectLineConversationContext } from '@sa/olivia-contract';
import { createStyleSet } from 'botframework-webchat';
import React, { useCallback, useMemo, useRef } from 'react';
import { adaptiveCardsHostConfig, webChatStyleOptions } from './customStyles';
import { createClientContext, createOliviaUserContext, oliviaStartMessageFilter } from './integration';

const OliviaWebChat = () => {
  const token = useRef(getQueryParam('Token') || '');
  const postConversationStarted = usePostConversationStarted(token);

  const onComponentMounted = useCallback(() => window.parent.postMessage({ from: 'bot', type: 'ready' }, '*'), []);
  const onDirectLineConnected = useCallback(
    () => window.parent.postMessage({ from: 'bot', type: 'connected' }, '*'),
    []
  );
  const onConversationStarted = useCallback(async (webChatConfig, trigger) => {
    const eventData: OliviaDirectLineConversationContext = {
      ...mapConversationContext(webChatConfig),
      userContext: { crn: '', name: '' }
    };

    if (typeof trigger !== 'string' && trigger.data?.query) {
      eventData.clientContext = createClientContext(trigger.data.query);
      eventData.userContext = createOliviaUserContext(trigger.data.query);
    }

    await postConversationStarted(eventData);
  }, []);

  const { loading, directLine, store } = useWebChatBootstrap({
    isConversationTrigger: oliviaStartMessageFilter,
    getWebChatConfig: useGetWebChatConfig(token),
    onComponentMounted,
    onDirectLineConnected,
    onConversationStarted
  });

  const renderMarkdown = useMemo(
    () =>
      buildMarkdownRenderer({
        respectCRLF: !!webChatStyleOptions.markdownRespectCRLF,
        linkTargetPlugin: { enabled: false }
      }),
    [webChatStyleOptions]
  );

  const webchatContainerRef = useRef<HTMLDivElement>(null);
  useCustomSendbox(webchatContainerRef);

  const styleSet = useMemo(
    () => ({
      ...createStyleSet(webChatStyleOptions),
      // Workaround for bug in webchat not hiding upload button
      // https://stackoverflow.com/questions/65083636/microsoft-botframework-styleset-with-hideuploadbutton
      uploadButton: {
        display: 'none !important'
      }
    }),
    []
  );

  return (
    <div className={'h-100'}>
      {loading && <LoadingState />}
      <div className={'h-100' + (loading ? ' d-none' : '')}>
        {directLine && (
          <div className={'h-100'} ref={webchatContainerRef}>
            <BFFWebChat
              activityMiddleware={[
                skipReactionActivitiesMiddleware,
                adaptiveCardARIARoleMessageMiddleware,
                androidAutoScrollBugMiddleware,
                bffNubInjectorActivityMiddleware,
                openLinkedIntentUrlMiddleware,
                openUrlEventHandlerMiddleware,
                markdownLinkTargetMiddleware()
              ]}
              overrideLocalizedStrings={() => removeKeyboardInstructionStrings}
              activityStatusMiddleware={ActivityStatusMiddleware(webChatStyleOptions)}
              adaptiveCardsHostConfig={adaptiveCardsHostConfig}
              attachmentMiddleware={bffAnswerAttachmentMiddleware}
              directLine={directLine}
              store={store}
              styleOptions={webChatStyleOptions}
              extraStyleSet={styleSet}
              renderMarkdown={renderMarkdown}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default OliviaWebChat;
